import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { ErrorMessage } from "../../components/error-message";
import {
  PaymentStatus,
  PaymentStatusColors,
  PaymentStatusTranslations,
  PaymentType,
  PaymentTypeColors,
  PaymentTypeTranslations,
  PaymentWithDetails,
} from "../../types/payments.types";
import { PaymentsAPI } from "../../APIs/payments.api";
import { Loading } from "../../components/loading";
import Label from "../../components/label";
import { TeamLink } from "../../components/team-link";
import {
  ArrowLeft,
  Wallet,
  User,
  CreditCard,
  Banknote,
  ArrowLeftRight,
  Check,
  AlertTriangle,
} from "lucide-react";
import { Button } from "../../components/button";
import { useToast } from "../../components/toast";

export function PaymentDetailsView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [payment, setPayment] = useState<PaymentWithDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { error, handleError, clearError } = useErrorHandler();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedAction, setSelectedAction] = useState<
    "confirm" | "reject" | ""
  >("");
  const [adminNotes, setAdminNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const { addToast } = useToast();

  useEffect(() => {
    if (!id) {
      navigate("/dashboard/payments");
      return;
    }

    const fetchPaymentDetails = async () => {
      clearError();
      setIsLoading(true);
      try {
        const response = await PaymentsAPI.getPayment(parseInt(id));
        if (response.statusCode === 200) {
          setPayment(response.data);
        } else {
          handleError(response.message, { persist: true });
        }
      } catch (err) {
        handleError("Error de conexión, intente nuevamente.", {
          persist: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [id, handleError, clearError, navigate]);

  const handleTransferAction = async () => {
    if (!payment || !payment.transfer_payment_id || !selectedAction) {
      return;
    }

    setIsSubmitting(true);
    clearError();

    try {
      let response;
      if (selectedAction === "confirm") {
        response = await PaymentsAPI.confirmTransferPayment(
          payment.transfer_payment_id,
          {
            admin_notes: adminNotes || undefined,
          }
        );
      } else {
        response = await PaymentsAPI.rejectTransferPayment(
          payment.transfer_payment_id,
          {
            rejection_reason: rejectionReason,
            admin_notes: adminNotes || undefined,
          }
        );
      }

      if (response.statusCode === 200) {
        addToast({
          type: "success",
          message: "Acción realizada correctamente",
          location: "bottom-center",
        });
        const refreshResponse = await PaymentsAPI.getPayment(parseInt(id!));
        if (refreshResponse.statusCode === 200) {
          setPayment(refreshResponse.data);
        }

        setSelectedAction("");
        setAdminNotes("");
        setRejectionReason("");
      } else {
        handleError(response.message, { persist: true });
      }
    } catch (err) {
      handleError("Error al procesar la acción, intente nuevamente.", {
        persist: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedAction("");
    setAdminNotes("");
    setRejectionReason("");
  };

  const getPaymentTypeIcon = (type: PaymentType) => {
    switch (type) {
      case PaymentType.MERCADOPAGO:
        return <CreditCard className="h-5 w-5" />;
      case PaymentType.CASH:
        return <Banknote className="h-5 w-5" />;
      case PaymentType.TRANSFER:
        return <ArrowLeftRight className="h-5 w-5" />;
      default:
        return <Wallet className="h-5 w-5" />;
    }
  };

  const formatAmount = (amount: string | null | number | undefined) => {
    if (!amount) return "-";
    return `$ ${parseFloat(String(amount)).toLocaleString("es-AR")}`;
  };

  const formatDate = (date: string | null | Date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  if (isLoading) {
    return <Loading />;
  }

  if (!payment) {
    return (
      <div className="w-full p-8">
        <div className="text-center">
          <p className="text-gray-500">Pago no encontrado</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            Volver a pagos
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="w-full p-8">
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-4 text-sm sm:text-base"
        >
          <ArrowLeft className="h-4 w-4 mr-2 flex-shrink-0" />
          <span className="truncate">Volver a pagos</span>
        </button>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center min-w-0">
            <Wallet className="h-6 w-6 sm:h-8 sm:w-8 text-gray-700 mr-3 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">
                Pago #{payment.payment_id}
              </h1>
              <p className="text-sm sm:text-base text-gray-600 break-words">
                Detalles del pago para {payment.team_name}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 lg:flex-shrink-0">
            <Label color={PaymentTypeColors[payment.payment_type] as any}>
              <div className="flex items-center gap-2">
                {getPaymentTypeIcon(payment.payment_type)}
                {PaymentTypeTranslations[payment.payment_type]}
              </div>
            </Label>
            <Label
              color={
                PaymentStatusColors[payment.payment_status as PaymentStatus]
              }
            >
              {PaymentStatusTranslations[
                payment.payment_status as PaymentStatus
              ] || payment.payment_status}
            </Label>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4">
          <ErrorMessage message={error} dismissible />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Información General
          </h2>

          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Equipo</dt>
              <dd className="mt-1">
                <TeamLink
                  teamName={payment.team_name}
                  teamId={payment.team_id}
                />
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500">
                Número de Registro
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {payment.registration_number}
              </dd>
            </div>

            {payment.team_number && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Número de Equipo
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {payment.team_number}
                </dd>
              </div>
            )}

            <div>
              <dt className="text-sm font-medium text-gray-500">Monto</dt>
              <dd className="mt-1 text-lg font-semibold text-gray-900">
                {formatAmount(payment.payment_amount)}
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500">
                Fecha de Creación
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {formatDate(payment.payment_created_at)}
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500">
                Última Actualización
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {formatDate(payment.payment_updated_at)}
              </dd>
            </div>
          </dl>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Detalles del Método de Pago
          </h2>

          {payment.payment_type === PaymentType.MERCADOPAGO && (
            <dl className="space-y-4">
              {payment.mercadopago_payment_id ? (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    ID MercadoPago
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">
                    {payment.mercadopago_payment_id}
                  </dd>
                </div>
              ) : (
                <div className="text-sm text-gray-500">
                  No hay información adicional disponible para este pago de
                  MercadoPago.
                </div>
              )}
            </dl>
          )}

          {payment.payment_type === PaymentType.CASH && (
            <dl className="space-y-4">
              <div className="flex items-start">
                <User className="h-4 w-4 text-gray-400 mt-0.5 mr-2" />
                <div>
                  <dt className="text-sm font-medium text-gray-500">Pagador</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {payment.payer_first_name} {payment.payer_last_name}
                  </dd>
                </div>
              </div>

              {payment.payer_dni && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">DNI</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">
                    {payment.payer_dni}
                  </dd>
                </div>
              )}

              {payment.received_date && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Fecha de Recepción
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(payment.received_date)}
                  </dd>
                </div>
              )}

              {payment.received_by_username && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Recibido por
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {payment.received_by_username}
                  </dd>
                </div>
              )}
            </dl>
          )}

          {payment.payment_type === PaymentType.TRANSFER && (
            <dl className="space-y-4">
              <div className="flex items-start">
                <User className="h-4 w-4 text-gray-400 mt-0.5 mr-2" />
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Remitente
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {payment.sender_first_name} {payment.sender_last_name}
                  </dd>
                </div>
              </div>

              {payment.sender_cuit && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">CUIT</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">
                    {payment.sender_cuit}
                  </dd>
                </div>
              )}

              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Fecha de Transferencia
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(payment.payment_created_at!)}
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Estado de Confirmación
                </dt>
                <dd className="mt-1">
                  <Label
                    color={
                      payment.transfer_confirmation_status === "confirmed"
                        ? "green"
                        : payment.transfer_confirmation_status === "rejected"
                        ? "red"
                        : "yellow"
                    }
                  >
                    {payment.transfer_confirmation_status === "confirmed"
                      ? "Confirmada"
                      : payment.transfer_confirmation_status === "rejected"
                      ? "Rechazada"
                      : "Pendiente"}
                  </Label>
                </dd>
              </div>

              {payment.transfer_confirmation_status === "confirmed" &&
                payment.transfer_confirmed_at && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Confirmada el
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formatDate(payment.transfer_confirmed_at)}
                    </dd>
                  </div>
                )}

              {payment.transfer_confirmation_status === "confirmed" &&
                payment.transfer_confirmed_by_username && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Confirmada por
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {payment.transfer_confirmed_by_username}
                    </dd>
                  </div>
                )}

              {payment.transfer_confirmation_status === "rejected" &&
                payment.transfer_rejection_reason && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Motivo de Rechazo
                    </dt>
                    <dd className="mt-1 text-sm text-red-600">
                      {payment.transfer_rejection_reason}
                    </dd>
                  </div>
                )}

              {payment.transfer_admin_notes && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Notas del Administrador
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {payment.transfer_admin_notes}
                  </dd>
                </div>
              )}
            </dl>
          )}
        </div>

        {payment.payment_type === PaymentType.TRANSFER &&
          payment.payment_status === "pending" &&
          payment.transfer_confirmation_status === "pending" && (
            <div className="lg:col-span-2">
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Acciones de Transferencia
                </h2>

                <form className="space-y-4" onSubmit={handleTransferAction}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Acción
                    </label>
                    <select
                      value={selectedAction}
                      onChange={(e) =>
                        setSelectedAction(
                          e.target.value as "confirm" | "reject" | ""
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Seleccionar acción...</option>
                      <option value="confirm">Confirmar transferencia</option>
                      <option value="reject">Rechazar transferencia</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notas del Administrador (opcional)
                    </label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Agregar notas adicionales..."
                    />
                  </div>

                  {selectedAction === "reject" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Motivo de Rechazo{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        rows={3}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Especificar el motivo del rechazo..."
                      />
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="submit"
                      text={
                        isSubmitting
                          ? "Procesando..."
                          : selectedAction === "confirm"
                          ? "Confirmar Transferencia"
                          : selectedAction === "reject"
                          ? "Rechazar Transferencia"
                          : "Ejecutar Acción"
                      }
                      variant={
                        selectedAction === "confirm"
                          ? "create"
                          : selectedAction === "reject"
                          ? "delete"
                          : "primary"
                      }
                      disabled={!selectedAction || isSubmitting}
                      icon={
                        selectedAction === "confirm" ? (
                          <Check className="w-4 h-4 mr-2" />
                        ) : selectedAction === "reject" ? (
                          <AlertTriangle className="w-4 h-4 mr-2" />
                        ) : (
                          <ArrowLeftRight className="w-4 h-4 mr-2" />
                        )
                      }
                    />

                    <button
                      onClick={resetForm}
                      disabled={isSubmitting}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}
